export default class DateUtils {
  static format(originDate, formatCode) {
    if (!originDate) {
      return '';
    }
    const date = new Date(originDate);
    let format = formatCode;
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds(),
    };
    if (/(y+)/.test(format)) {
      format = format.replace(RegExp.$1, `${date.getFullYear()}`.substr(4 - RegExp.$1.length));
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const k in o) {
      if (new RegExp(`(${k})`).test(format)) {
        format = format.replace(RegExp.$1, (RegExp.$1.length === 1)
          ? (o[k]) : `00${o[k]}`.substr((`${o[k]}`).length));
      }
    }

    return format;
  }

  static formatDuration(duration) {
    const time = parseInt(duration / 1000, 10);
    let seconds = 0;
    let minutes = 0;
    let hours = 0;
    if (time) {
      if (time > 60 && time < 60 * 60) {
        minutes = parseInt(time / 60, 10);
        seconds = parseInt(time - minutes * 60, 10);
      } else if (time >= 60 * 60) {
        hours = parseInt(time / 60 / 60, 10);
        minutes = parseInt(time / 60, 10) - hours * 60;
        seconds = time - hours * 60 * 60 - minutes * 60;
      } else {
        seconds = time;
      }
    }
    if (hours < 10) {
      hours = `0${hours}`;
    }
    if (minutes < 10) {
      minutes = `0${minutes}`;
    }
    if (seconds < 10) {
      seconds = `0${seconds}`;
    }
    return { hours, minutes, seconds };
  }

  // 将yyyy-MM-dd HH:mm:ss这种格式的日期转换为Date对象
  static parseDate(dateStr) {
    if (!dateStr) {
      return null;
    }
    const [datePart, timePart] = dateStr.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart ? timePart.split(':').map(Number) : [0, 0, 0];

    return new Date(year, month - 1, day, hour, minute, second);
  }

  // 计算两个日期，在月份维度上的差值，date1 - date2
  static diffMonth(date1, date2) {
    const date1Year = date1.getYear();
    const date1Month = date1.getMonth();

    const date2Year = date2.getYear();
    const date2Month = date2.getMonth();

    return (date1Year * 12 + date1Month) - (date2Year * 12 + date2Month);
  }

  // 计算日期的月首
  static startOfMonth(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1);
  }

  static endOfMonth(date) {
    let year = date.getFullYear();
    let month = date.getMonth() + 1;
    const lastDate = new Date(new Date(year, month, 1, 0, 0, 0) - 1);
    year = lastDate.getFullYear();
    month = lastDate.getMonth();
    const day = lastDate.getDate();
    return new Date(year, month, day);
  }

  // 在date上增加offset个月份，会自动纠正月末的日期
  static addMonth(date, offset) {
    let year = date.getFullYear();
    let month = date.getMonth();
    let day = date.getDate();

    month = year * 12 + month + offset;
    year = Math.floor(month / 12);
    month %= 12;
    const lastDayOfMonth = DateUtils.endOfMonth(new Date(year, month, 1));
    const maxDay = (lastDayOfMonth.getDate());
    day = Math.min(day, maxDay);

    return new Date(year, month, day);
  }

  static addDay(date, offset) {
    return new Date((+date) + offset * 24 * 60 * 60 * 1000);
  }

  static addHours(date, offset) {
    return new Date((+date) + offset * 60 * 60 * 1000);
  }

  static startOfDay(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    return new Date(year, month, day, 0, 0, 0);
  }

  static endOfDay(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    return new Date(year, month, day, 23, 59, 59);
  }

  static getDaysFromDuration(start, end) {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    const result = [];
    if (startMs > endMs) {
      return result;
    }
    const dayMs = 1000 * 60 * 60 * 24;
    let validatedDate = new Date(start);
    while (validatedDate.getTime() <= endMs) {
      result.push(validatedDate);
      validatedDate = new Date(validatedDate.getTime() + dayMs);
    }
    return result;
  }

  static millsFormat(millSeconds) {
    if ((millSeconds / 1000) > 0 && (millSeconds / 1000) < 60) {
      // eslint-disable-next-line no-bitwise
      return `${~~(millSeconds / 1000)}秒`;
    }

    // eslint-disable-next-line no-bitwise
    const temp = ~~(millSeconds / (1000 * 60)); // 分钟
    if (temp <= 0) {
      return '0小时0分钟';
    }
    // eslint-disable-next-line no-bitwise
    const hour = ~~(temp / 60);
    const resutMin = temp % 60;
    return `${hour}小时${resutMin}分钟`;
  }

  static makeTimeReadable = ({
    target, readableWords = [], readableWordsBefore = [],
    dateFormatter, timeFormatter, needSpace = true,
  }) => {
    if (!target) return '';
    const _readableWords = readableWords === false ? readableWords : ([...readableWordsBefore, ...readableWords] || ['今天', '明天']);
    const _timeFormatter = timeFormatter || 'HH:mm';
    const _dateFormatter = dateFormatter || 'yyyy-MM-dd';
    const todayStartTime = DateUtils.startOfDay(new Date());
    const targetStartTime = DateUtils.startOfDay(target);
    let deltaDay = (targetStartTime - todayStartTime) / 86400000;
    deltaDay += readableWordsBefore.length;
    let dayString = '';
    if (_readableWords === false) {
      dayString = '';
    } else {
      dayString = (deltaDay >= 0 && deltaDay < _readableWords.length)
        ? _readableWords[deltaDay] : DateUtils.format(target, _dateFormatter);
    }
    if (needSpace) {
      return `${dayString} ${DateUtils.format(target, _timeFormatter)}`;
    }
    return `${dayString}${DateUtils.format(target, _timeFormatter)}`;
  };

  static roundingMinute=(time, rounding = 5) => {
    const currentMinute = time.getMinutes();
    const offsetMinute = rounding - (currentMinute % rounding);
    return this.addMinutes(time, offsetMinute);
  }

  static addDay=(date, offset) => new Date(+date + offset * 24 * 60 * 60 * 1000)

  static addHours=(date, offset) => new Date(+date + offset * 60 * 60 * 1000)

  static addMinutes=(date, offset) => new Date(+date + offset * 60 * 1000)
}
